import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, HeartIcon, CheckIcon, MinusIcon, PlusIcon, ShoppingBagIcon, Share2Icon } from './icons';
import type { ProductDetail, ProductDetailVariant, ProductDetailProductOption, ProductDetailProductOptionRender, WidgetAuth } from '../types/api';
import type { CartItem } from '../types/app';
import { calcFullPrice, extractMoney } from '../utils/calcFullPrice';
import { useComboProducts } from '../hooks/useComboProducts';
import { ComboProductSelectors } from './ComboProductSelectors';

interface ProductDetailOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  productId: number | null;
  product: ProductDetail | null;
  isLoading: boolean;
  error: string | null;
  onAddToBag: (productId: number, variantId: number, quantity: number, selectedProductOptions?: Record<string, string>) => void;
  onAddComboBag?: (items: Array<{ productId: number; variantId: number; discount: number }>) => void;
  // QA2/BUG-2: cancelling a reservation. When the selected variant is already in
  // the bag the primary button becomes a toggle and calls this with the basket
  // entry id (resolved via findInCart) instead of onAddToBag. Cancel REMOVES the
  // reservation outright — this is a reserve, not a quantity cart, so we never
  // decrement. Wired to useCart.removeItem.
  onRemoveFromBag?: (basketProductId: number) => void;
  // SPEC-064: variant-aware signatures. Callers source variantId from the PDP's
  // selectedVariant so that sibling variants of the same product render independently.
  onToggleFavorite: (productId: number, variantId: number) => void;
  isInCart: (productId: number, variantId: number) => boolean;
  isWishlisted: (productId: number, variantId: number) => boolean;
  // Composite-key cart lookup — when set, PDP initializes (and re-syncs on
  // variant change) its quantity state from the existing cart entry instead
  // of defaulting to 1. Required for the "open PDP for an item already in
  // bag → PDP shows matching qty" UX. Threaded from ConsultationScreen's
  // cart.findInCart.
  findInCart?: (productId: number, variantId: number) => CartItem | undefined;
  // Threaded from ConsultationScreen's useCart instance so PDP does not spawn
  // a disconnected second hook. Called (debounced) when the user adjusts quantity
  // for a variant that is already in cart.
  onCartQuantityChange?: (productId: number | string, variantId: number | string, newQty: number) => void;
  onRetry?: () => void;
  /**
   * Share to chat. `variant` is the variant on screen, or null when the selected
   * options match no variant (the product card is shared then).
   */
  onShareProduct?: (product: ProductDetail, variant: ProductDetailVariant | null) => void;
  initialVariantId?: number;
  /**
   * Bumped by the parent to re-apply initialVariantId when it has not changed —
   * e.g. the same chat card clicked again after the customer picked other options.
   */
  initialVariantRequest?: number;
  auth?: WidgetAuth;
}

// ─── Helpers ────────────────────────────────────────────────────────

/**
 * Check if a variant is the single "Default Title" variant that should be hidden.
 */
function isDefaultVariantOnly(variants: ProductDetailVariant[] | undefined): boolean {
  if (!variants || variants.length === 0) return true;
  if (variants.length === 1 && variants[0].name?.toLowerCase() === 'default title') return true;
  return false;
}

/**
 * Extract unique option groups from variants.
 * e.g., [{name: "Color", values: ["Black", "Tan"]}, {name: "Size", values: ["Medium", "Large"]}]
 */
function getOptionGroups(variants: ProductDetailVariant[]): { name: string; values: (string | number)[] }[] {
  const groups = new Map<string, Set<string | number>>();
  for (const variant of variants) {
    for (const opt of variant.options) {
      if (!groups.has(opt.name)) {
        groups.set(opt.name, new Set());
      }
      groups.get(opt.name)!.add(opt.value);
    }
  }
  return Array.from(groups.entries()).map(([name, valuesSet]) => ({
    name,
    values: Array.from(valuesSet),
  }));
}

/**
 * Find a variant that matches the given selected options.
 */
function findVariantByOptions(
  variants: ProductDetailVariant[],
  selectedOptions: Record<string, string | number>
): ProductDetailVariant | undefined {
  return variants.find((variant) =>
    variant.options.every((opt) => selectedOptions[opt.name] === opt.value)
  );
}

/**
 * Group raw product_options into renderable option groups (mirrors Angular's getVariantProductOptionsRender).
 * Groups options by identifier; uses previousSelections to preserve user selections across re-renders.
 */
function buildProductOptionGroups(
  productOptions: ProductDetailProductOption[] | undefined,
  previousSelections: Record<string, string>
): ProductDetailProductOptionRender[] {
  if (!productOptions || productOptions.length === 0) return [];

  const groupMap = new Map<string, ProductDetailProductOption[]>();
  for (const option of productOptions) {
    if (!groupMap.has(option.identifier)) {
      groupMap.set(option.identifier, []);
    }
    groupMap.get(option.identifier)!.push(option);
  }

  return Array.from(groupMap.entries()).map(([identifier, options]) => {
    const firstOption = options[0];
    // Use previous selection if it exists and is still valid, otherwise default to first option
    const selectedValue =
      previousSelections[identifier] !== undefined &&
      options.some((o) => o.value === previousSelections[identifier])
        ? previousSelections[identifier]
        : firstOption.value;

    const selectedOption = options.find((o) => o.value === selectedValue) ?? firstOption;

    return {
      identifier,
      label: firstOption.description,
      value: selectedValue,
      options,
      description: firstOption.description,
      price: selectedOption.price,
    };
  });
}

// ─── Combo Loading Skeleton ─────────────────────────────────────────

function ComboSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-3 bg-brand-navy/10 rounded w-1/4 mb-2" />
      <div className="h-10 bg-brand-navy/10 rounded-sm w-full" />
      <div className="h-3 bg-brand-navy/10 rounded w-1/4 mb-2" />
      <div className="h-10 bg-brand-navy/10 rounded-sm w-full" />
    </div>
  );
}

// ─── Skeleton ───────────────────────────────────────────────────────

function PdpSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto px-8 py-6 animate-pulse">
      {/* Image placeholder */}
      <div className="aspect-square bg-brand-navy/10 rounded-sm mb-4" />
      {/* Thumbnail row */}
      <div className="flex gap-2 mb-6">
        <div className="w-14 h-14 bg-brand-navy/10 rounded-sm" />
        <div className="w-14 h-14 bg-brand-navy/10 rounded-sm" />
        <div className="w-14 h-14 bg-brand-navy/10 rounded-sm" />
      </div>
      {/* Title */}
      <div className="h-3 bg-brand-navy/10 rounded w-1/3 mb-2" />
      <div className="h-5 bg-brand-navy/10 rounded w-3/4 mb-2" />
      {/* Price */}
      <div className="h-4 bg-brand-navy/10 rounded w-1/4 mb-6" />
      {/* Variant selectors */}
      <div className="h-3 bg-brand-navy/10 rounded w-1/5 mb-2" />
      <div className="flex gap-2 mb-4">
        <div className="w-16 h-8 bg-brand-navy/10 rounded-sm" />
        <div className="w-16 h-8 bg-brand-navy/10 rounded-sm" />
        <div className="w-16 h-8 bg-brand-navy/10 rounded-sm" />
      </div>
      {/* Description */}
      <div className="space-y-2 mb-4">
        <div className="h-3 bg-brand-navy/10 rounded w-full" />
        <div className="h-3 bg-brand-navy/10 rounded w-5/6" />
        <div className="h-3 bg-brand-navy/10 rounded w-2/3" />
      </div>
    </div>
  );
}

// ─── Image Gallery ──────────────────────────────────────────────────

function ImageGallery({
  images,
  fallbackImage,
  activeIndex,
  onIndexChange,
}: {
  images: string[];
  fallbackImage?: string;
  activeIndex: number;
  onIndexChange: (index: number) => void;
}) {
  const allImages = images.length > 0 ? images : fallbackImage ? [fallbackImage] : [];

  if (allImages.length === 0) {
    return (
      <div className="aspect-square bg-brand-navy/5 rounded-sm flex items-center justify-center mb-4">
        <ShoppingBagIcon className="w-16 h-16 text-brand-navy/20" />
      </div>
    );
  }

  const currentImage = allImages[activeIndex] || allImages[0];

  return (
    <div className="mb-4">
      {/* Main image */}
      <div className="aspect-square bg-white rounded-sm overflow-hidden mb-3">
        <img
          src={currentImage}
          alt="Product"
          className="w-full h-full object-contain"
        />
      </div>
      {/* Thumbnails */}
      {allImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {allImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => onIndexChange(idx)}
              className={`w-14 h-14 rounded-sm overflow-hidden flex-shrink-0 border-2 transition-colors ${
                idx === activeIndex
                  ? 'border-brand-navy'
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Variant Selectors ──────────────────────────────────────────────

function VariantSelectors({
  optionGroups,
  selectedOptions,
  onOptionChange,
  variants,
}: {
  optionGroups: { name: string; values: (string | number)[] }[];
  selectedOptions: Record<string, string | number>;
  onOptionChange: (name: string, value: string | number) => void;
  variants: ProductDetailVariant[];
}) {
  return (
    <div className="space-y-4">
      {optionGroups.map((group) => (
        <div key={group.name}>
          <p className="text-xs uppercase tracking-[0.15em] text-brand-navy/60 mb-2">
            {group.name}
          </p>
          <div className="flex flex-wrap gap-2">
            {group.values.map((value) => {
              const isSelected = selectedOptions[group.name] === value;
              // Check if this option value leads to any valid variant
              const testOptions = { ...selectedOptions, [group.name]: value };
              const matchingVariant = findVariantByOptions(variants, testOptions);
              const isAvailable = matchingVariant !== undefined;

              return (
                <button
                  key={String(value)}
                  onClick={() => onOptionChange(group.name, value)}
                  disabled={!isAvailable}
                  className={`px-3 py-1.5 text-sm border rounded-sm transition-colors ${
                    isSelected
                      ? 'bg-brand-navy text-white border-brand-navy'
                      : isAvailable
                        ? 'border-brand-gold/30 text-brand-navy hover:border-brand-navy'
                        : 'border-brand-gold/10 text-brand-navy/30 cursor-not-allowed'
                  }`}
                >
                  {String(value)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Product Option Selectors (SPEC-035) ────────────────────────────

function ProductOptionSelectors({
  optionGroups,
  onOptionChange,
}: {
  optionGroups: ProductDetailProductOptionRender[];
  onOptionChange: (identifier: string, value: string) => void;
}) {
  if (optionGroups.length === 0) return null;

  return (
    <div className="space-y-4">
      {optionGroups.map((group) => (
        <div key={group.identifier}>
          <p className="text-xs uppercase tracking-[0.15em] text-brand-navy/60 mb-2">
            {group.label}
          </p>
          <select
            value={group.value}
            onChange={(e) => onOptionChange(group.identifier, e.target.value)}
            className="w-full border border-brand-gold/30 rounded-sm px-3 py-2 text-sm text-brand-navy bg-transparent focus:outline-none focus:border-brand-navy transition-colors"
          >
            {group.options.map((option) => {
              const { amount } = extractMoney(option.price);
              const priceLabel = amount > 0 ? ` (+${option.price})` : '';
              return (
                <option key={option.value} value={option.value}>
                  {option.name}{priceLabel}
                </option>
              );
            })}
          </select>
        </div>
      ))}
    </div>
  );
}

// ─── Quantity Selector ──────────────────────────────────────────────

function QuantitySelector({
  quantity,
  maxQuantity,
  onQuantityChange,
}: {
  quantity: number;
  maxQuantity: number | null; // null means unlimited
  onQuantityChange: (q: number) => void;
}) {
  return (
    <div className="inline-flex w-fit items-center gap-1 border border-brand-gold/20 rounded-sm">
      <button
        onClick={() => onQuantityChange(quantity - 1)}
        disabled={quantity <= 1}
        className="p-2 text-brand-navy disabled:text-brand-navy/20 hover:bg-brand-navy/5 transition-colors"
      >
        <MinusIcon className="w-4 h-4" />
      </button>
      <span className="w-8 text-center text-sm font-medium text-brand-navy">
        {quantity}
      </span>
      <button
        onClick={() => onQuantityChange(quantity + 1)}
        disabled={maxQuantity !== null && quantity >= maxQuantity}
        className="p-2 text-brand-navy disabled:text-brand-navy/20 hover:bg-brand-navy/5 transition-colors"
      >
        <PlusIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Description ────────────────────────────────────────────────────

function ProductDescription({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [needsTruncation, setNeedsTruncation] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      setNeedsTruncation(contentRef.current.scrollHeight > 120);
    }
  }, [html]);

  return (
    <div className="mb-4">
      <div
        ref={contentRef}
        className={`text-sm text-brand-navy/80 leading-relaxed overflow-hidden transition-all ${
          !expanded && needsTruncation ? 'max-h-[120px]' : 'max-h-[2000px]'
        }`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {needsTruncation && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs text-brand-gold mt-1 hover:underline"
        >
          {expanded ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

export function ProductDetailOverlay({
  isOpen,
  onClose,
  // SPEC-064: productId prop retained for backward compat / call-site contract,
  // but the body now reads product.id + selectedVariant.id. Underscore prefix
  // silences the no-unused-vars rule without narrowing the public API.
  productId: _productId,
  product,
  isLoading,
  error,
  onAddToBag,
  onAddComboBag,
  onRemoveFromBag,
  onToggleFavorite,
  isInCart,
  isWishlisted,
  findInCart,
  onCartQuantityChange,
  onRetry,
  onShareProduct,
  initialVariantId,
  initialVariantRequest,
  auth,
}: ProductDetailOverlayProps) {
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string | number>>({});
  const [selectedProductOptions, setSelectedProductOptions] = useState<Record<string, string>>({});
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  // Ref-backed debounce timer for cart qty-sync (Track A). ~400ms window collapses
  // rapid +/+/+ clicks into a single updateQuantity call with the final value.
  const cartQtyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // QA2/BUG-2: in-flight guard for the primary Reserve/Reserved button. Mirrors
  // the reservingProductId guard ConsultationScreen.handleReserve uses for cards,
  // so a double-click can neither double-add nor add-then-cancel.
  const [bagActionPending, setBagActionPending] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Hover/focus affordance for the "Reserved" state — signals the button cancels.
  const [reservedHovered, setReservedHovered] = useState(false);

  // Combo products hook (SPEC-036)
  // selectedVariant is derived below, but we need to initialize with null first
  // We'll derive selectedVariant here early for useComboProducts
  const hasVariantsEarly = !isDefaultVariantOnly(product?.product_variants);
  const variantsEarly = product?.product_variants || [];
  const selectedVariantEarly = useMemo(() => {
    if (!hasVariantsEarly || Object.keys(selectedOptions).length === 0) {
      return variantsEarly[0] || null;
    }
    return findVariantByOptions(variantsEarly, selectedOptions) || variantsEarly[0] || null;
  }, [hasVariantsEarly, variantsEarly, selectedOptions]);

  const {
    comboProducts,
    isLoading: comboLoading,
    selectedComboVariants,
    updateComboVariant,
    hasComboProducts,
    comboDiscount,
    reset: resetCombo,
  } = useComboProducts(product, selectedVariantEarly, auth ?? null);

  // Derive product display name (API may use 'name' or 'title')
  const displayName = product?.title || product?.name || '';

  // Determine if this product has meaningful variants
  // (reuse hasVariantsEarly / variantsEarly / selectedVariantEarly computed above for combo hook)
  const hasVariants = hasVariantsEarly;
  const variants = variantsEarly;
  const selectedVariant = selectedVariantEarly;

  // The variant "Share to chat" sends. Unlike selectedVariant it does not fall back
  // to the first variant: an unavailable combination shares the product instead of
  // a variant the customer is not looking at. A product with only "Default Title"
  // shares that variant (its card name gets no label).
  const shareVariant = useMemo(
    () => (hasVariants ? findVariantByOptions(variants, selectedOptions) ?? null : variants[0] ?? null),
    [hasVariants, variants, selectedOptions]
  );

  // Extract option groups from variants
  const optionGroups = useMemo(
    () => (hasVariants ? getOptionGroups(variants) : []),
    [hasVariants, variants]
  );

  // Build product option groups from selected variant's product_options (SPEC-035)
  // Reset to defaults when variant changes (AC-13: variant change resets product options)
  const productOptionGroups = useMemo(
    () => buildProductOptionGroups(selectedVariant?.product_options, {}),
    [selectedVariant]
  );

  // Track B forward: scroll image gallery to the active variant's image whenever
  // the selected variant changes. Single effect subsumes the former init block and
  // the duplicate scroll inside handleOptionChange. activeImageIndex is intentionally
  // excluded from deps — this effect must only fire on variant identity change, not
  // on thumbnail click (reverse path sets activeImageIndex before mutating selectedOptions,
  // so imgIdx === activeImageIndex on the follow-up render and the guard below bails).
  useEffect(() => {
    if (!selectedVariant?.image || !product?.images?.length) return;
    const imgIdx = product.images.indexOf(selectedVariant.image);
    if (imgIdx >= 0 && imgIdx !== activeImageIndex) {
      setActiveImageIndex(imgIdx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant?.id, product?.images]);

  // Track A cleanup: cancel any pending cart-qty debounce when the selected variant
  // changes so a write queued for variant A never fires with variant B's identity.
  useEffect(() => {
    return () => {
      if (cartQtyDebounceRef.current !== null) {
        clearTimeout(cartQtyDebounceRef.current);
      }
    };
  }, [selectedVariant?.id]);

  // Initialize selected options when product loads; reset combo state.
  // When initialVariantId is provided, pre-select that variant's options and scroll
  // the image gallery to the variant's image (if it has one).
  useEffect(() => {
    if (product && hasVariants && variants.length > 0) {
      // Try to find the pre-selected variant when initialVariantId is provided
      const targetVariant = initialVariantId
        ? variants.find((v) => v.id === initialVariantId) ?? variants[0]
        : variants[0];

      const initial: Record<string, string | number> = {};
      for (const opt of targetVariant.options) {
        initial[opt.name] = opt.value;
      }
      setSelectedOptions(initial);
      // Image scroll is handled by the variant→image useEffect above;
      // reset to 0 here so the gallery resets on product change before that
      // effect fires with the new variant's id.
      setActiveImageIndex(0);
      // Initial qty sync with cart: if this variant is already in basket,
      // show the existing qty so ± writes via Track A's debounced
      // onCartQuantityChange are deltas on the real value, not on 1.
      setQuantity(findInCart?.(product.id, targetVariant.id)?.quantity ?? 1);
    } else {
      setSelectedOptions({});
      setActiveImageIndex(0);
      setQuantity(1);
    }
    // Reset product options when product changes
    setSelectedProductOptions({});
    // Reset combo state when product changes (useComboProducts handles reload internally)
    resetCombo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, initialVariantId, initialVariantRequest]);

  // When variant changes, reset quantity and product options.
  // Image scroll is now handled exclusively by the variant→image useEffect
  // (Track B forward), not inline here — avoids duplicate scroll calls.
  const handleOptionChange = useCallback(
    (name: string, value: string | number) => {
      const newOptions = { ...selectedOptions, [name]: value };
      setSelectedOptions(newOptions);
      // Sync qty from cart for the newly-selected variant — if it's already in
      // basket with N, show N; otherwise default to 1 (AC-20 intent preserved:
      // qty resets across variant change, now cart-aware).
      const newVariant = findVariantByOptions(variants, newOptions);
      setQuantity(
        newVariant && findInCart && product
          ? findInCart(product.id, newVariant.id)?.quantity ?? 1
          : 1
      );
      setSelectedProductOptions({}); // AC-13: reset product options on variant change
    },
    [selectedOptions, variants, findInCart, product]
  );

  // Handle product option selection change (SPEC-035)
  const handleProductOptionChange = useCallback((identifier: string, value: string) => {
    setSelectedProductOptions((prev) => ({ ...prev, [identifier]: value }));
  }, []);

  // Build the effective selectedProductOptions map for price calculation and add-to-bag
  // Merge productOptionGroups defaults with user selections
  const effectiveProductOptions = useMemo(() => {
    const effective: Record<string, string> = {};
    for (const group of productOptionGroups) {
      effective[group.identifier] =
        selectedProductOptions[group.identifier] ?? group.value;
    }
    return effective;
  }, [productOptionGroups, selectedProductOptions]);

  // Determine price display (SPEC-035: use calcFullPrice when product options exist)
  const rawDisplayPrice = selectedVariant?.price || product?.price;
  const hasProductOptions =
    selectedVariant?.product_options && selectedVariant.product_options.length > 0;

  const computedPrice = useMemo(() => {
    if (!rawDisplayPrice) return null;
    if (hasProductOptions) {
      return calcFullPrice(
        rawDisplayPrice,
        selectedVariant?.product_options,
        effectiveProductOptions
      );
    }
    return null;
  }, [rawDisplayPrice, hasProductOptions, selectedVariant, effectiveProductOptions]);

  const displayComparePrice = selectedVariant?.compare_at_price || product?.compare_at_price;

  // Inventory & stock status
  const inventory = selectedVariant?.inventory ?? 0;
  const allowOutOfStock = selectedVariant?.add_to_cart_when_out_of_stock ?? false;
  const isOutOfStock = hasVariants && inventory === 0 && !allowOutOfStock;

  // Max quantity for the selector
  const maxQuantity = allowOutOfStock ? null : (inventory > 0 ? inventory : 0);

  // Product images
  const images = product?.images || [];
  const fallbackImage = product?.image_url;

  // Cart/wishlist state — SPEC-064 composite key. The PDP must reflect the
  // SELECTED variant's state, not the product-level aggregate. Switching variant
  // flips "Reserved" -> "Reserve" and the heart fill in one render tick.
  const inCart = selectedVariant && product ? isInCart(product.id, selectedVariant.id) : false;
  const wishlisted = selectedVariant && product ? isWishlisted(product.id, selectedVariant.id) : false;

  // QA2/BUG-2: the "Reserved" button can only cancel when the host actually wired
  // the remove path. Without both props it stays cosmetic (old behaviour).
  const canCancelReserve = inCart && !!onRemoveFromBag && !!findInCart;
  const showCancelReserve = canCancelReserve && reservedHovered && !bagActionPending;

  // Collection display
  const collection = product?.collections?.value?.[0];

  const handleAddToBag = useCallback(async () => {
    if (!product) return;
    if (bagActionPending) return; // QA2/BUG-2: ignore clicks while add/remove is in flight
    if (hasVariants && (!selectedVariant || isOutOfStock)) return;
    const variantId = selectedVariant?.id ?? 0;

    // QA2/BUG-2: when this variant is already reserved the button is a TOGGLE —
    // a second click cancels the reservation rather than re-adding/incrementing.
    // Resolve the basket entry by composite (product, variant) key so sibling
    // variants of the same product cancel independently.
    if (inCart && onRemoveFromBag && findInCart) {
      const entry = findInCart(product.id, variantId);
      if (entry) {
        setBagActionPending(true);
        try {
          await onRemoveFromBag(entry.id);
        } finally {
          if (mountedRef.current) setBagActionPending(false);
        }
      }
      return;
    }

    // Pass effectiveProductOptions only when there are product options (SPEC-035 AC-15)
    const optionsToPass = hasProductOptions && Object.keys(effectiveProductOptions).length > 0
      ? effectiveProductOptions
      : undefined;
    setBagActionPending(true);
    try {
      await onAddToBag(product.id, variantId, quantity, optionsToPass);
    } finally {
      if (mountedRef.current) setBagActionPending(false);
    }
  }, [product, bagActionPending, hasVariants, selectedVariant, isOutOfStock, quantity, onAddToBag, hasProductOptions, effectiveProductOptions, inCart, onRemoveFromBag, findInCart]);

  /**
   * Handle combo "Add to Bag" action (SPEC-036).
   * Iterates selectedComboVariants, builds combo items with discount, calls onAddComboBag.
   * AC-16: one entry per combo product.
   * AC-17: discount_val = parent's combo_product_discount as number.
   * AC-19: main product NOT added; only child combo product variants.
   */
  const handleAddComboBag = useCallback(() => {
    if (!onAddComboBag || comboProducts.length === 0) return;
    const discount = Number(comboDiscount); // A-7: parse string to number
    const items = comboProducts
      .map((combo) => {
        const variant = selectedComboVariants[combo.product.id];
        if (!variant) return null;
        return {
          productId: combo.product.id,
          variantId: variant.id,
          discount,
        };
      })
      .filter((item): item is { productId: number; variantId: number; discount: number } => item !== null);

    if (items.length > 0) {
      onAddComboBag(items);
    }
  }, [onAddComboBag, comboProducts, selectedComboVariants, comboDiscount]);

  // Check if all combo products have a selected variant (for button disabled state)
  const allComboVariantsSelected = comboProducts.length > 0 &&
    comboProducts.every((combo) => !!selectedComboVariants[combo.product.id]);

  const handleToggleFavorite = useCallback(() => {
    if (!product) return;
    // SPEC-064: thread variantId so the toggle scopes to the selected variant,
    // not the entire product. selectedVariant?.id ?? 0 preserves the product-
    // level fallback for products with no real variants.
    onToggleFavorite(product.id, selectedVariant?.id ?? 0);
  }, [product, selectedVariant, onToggleFavorite]);

  // Track B reverse: thumbnail click → variant sync.
  // Sets activeImageIndex first (this satisfies the forward effect's
  // imgIdx !== activeImageIndex guard on the follow-up render so no double-fire
  // occurs). Then finds the variant whose image URL matches and updates
  // selectedOptions without calling handleOptionChange (loop avoidance).
  const handleGalleryIndexChange = useCallback(
    (idx: number) => {
      setActiveImageIndex(idx);
      if (!product?.images || !variants.length) return;
      const imageUrl = product.images[idx];
      if (!imageUrl) return;
      const matchedVariant = variants.find((v) => v.image === imageUrl);
      if (!matchedVariant || matchedVariant.id === selectedVariant?.id) return;
      const newOptions: Record<string, string | number> = {};
      for (const opt of matchedVariant.options) {
        newOptions[opt.name] = opt.value;
      }
      setSelectedOptions(newOptions);
      // Qty sync with cart for consistency with handleOptionChange path.
      setQuantity(
        findInCart ? findInCart(product.id, matchedVariant.id)?.quantity ?? 1 : 1
      );
      setSelectedProductOptions({});
    },
    [product, variants, selectedVariant, findInCart]
  );

  // Track A: quantity change handler with debounced cart-sync.
  // When the selected variant is already in cart, the cart line is updated
  // after a ~400ms debounce window so rapid clicks collapse into one call.
  // When the variant is not in cart, only local quantity state is updated.
  const handleQuantityChange = useCallback(
    (newQty: number) => {
      setQuantity(newQty);
      if (!onCartQuantityChange || !product || !selectedVariant) return;
      if (!isInCart(product.id, selectedVariant.id)) return;
      if (cartQtyDebounceRef.current !== null) {
        clearTimeout(cartQtyDebounceRef.current);
      }
      cartQtyDebounceRef.current = setTimeout(() => {
        cartQtyDebounceRef.current = null;
        onCartQuantityChange(product.id, selectedVariant.id, newQty);
      }, 400);
    },
    [onCartQuantityChange, product, selectedVariant, isInCart]
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0 bg-brand-cream/[0.98] backdrop-blur-sm flex flex-col shadow-2xl border border-brand-gold/20 z-30"
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-brand-gold/20 flex items-center justify-between">
            <h2 className="font-serif text-brand-navy truncate pr-4">
              {isLoading ? 'Loading...' : displayName}
            </h2>
            <button
              onClick={onClose}
              className="text-brand-navy/60 hover:text-brand-navy transition-colors flex-shrink-0"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <PdpSkeleton />
          ) : error ? (
            // Error state
            <div className="flex-1 flex flex-col items-center justify-center px-8">
              <p className="text-sm text-brand-navy/60 mb-4 text-center">{error}</p>
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="px-6 py-2 border border-brand-navy text-brand-navy text-sm uppercase tracking-[0.15em] rounded-sm hover:bg-brand-navy hover:text-white transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          ) : product ? (
            <>
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-8 py-6">
                {/* Image Gallery */}
                <ImageGallery
                  images={images}
                  fallbackImage={fallbackImage}
                  activeIndex={activeImageIndex}
                  onIndexChange={handleGalleryIndexChange}
                />

                {/* Collection */}
                {collection && (
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-gold mb-1">
                    {collection}
                  </p>
                )}

                {/* Title */}
                <h3 className="font-serif text-lg text-brand-navy mb-1">
                  {displayName}
                </h3>

                {/* Price (SPEC-035: computed price when product options present) */}
                <div className="flex items-center gap-2 mb-4">
                  {displayComparePrice && (
                    <span className="text-sm text-brand-navy/40 line-through">
                      {displayComparePrice}
                    </span>
                  )}
                  <span className="text-base font-medium text-brand-navy">
                    {computedPrice
                      ? computedPrice.priceStr
                      : rawDisplayPrice}
                  </span>
                </div>

                {/* SPEC-036: Combo products conditional rendering (AC-10, AC-11) */}
                {hasComboProducts ? (
                  /* Combo mode: show combo selectors instead of standard selectors */
                  <div className="mb-4">
                    {comboLoading ? (
                      /* AC-11: skeleton while combo products are loading */
                      <ComboSkeleton />
                    ) : (
                      /* AC-12: combo product variant selectors */
                      <ComboProductSelectors
                        comboProducts={comboProducts}
                        selectedComboVariants={selectedComboVariants}
                        onComboVariantChange={updateComboVariant}
                      />
                    )}
                  </div>
                ) : (
                  /* Standard mode: show variant selectors, product options, inventory, quantity */
                  <div className="space-y-4 mb-4">
                    {/* Variant Selectors (only when product has meaningful variants) */}
                    {hasVariants && optionGroups.length > 0 && (
                      <VariantSelectors
                        optionGroups={optionGroups}
                        selectedOptions={selectedOptions}
                        onOptionChange={handleOptionChange}
                        variants={variants}
                      />
                    )}

                    {/* Product Option Selectors (SPEC-035: add-on options, below variant selectors) */}
                    {productOptionGroups.length > 0 && (
                      <ProductOptionSelectors
                        optionGroups={productOptionGroups.map((group) => ({
                          ...group,
                          value: effectiveProductOptions[group.identifier] ?? group.value,
                        }))}
                        onOptionChange={handleProductOptionChange}
                      />
                    )}

                    {/* Inventory Status (only when product has variants) */}
                    {hasVariants && selectedVariant && (
                      <div>
                        {inventory > 0 ? (
                          <p className={`text-sm ${inventory <= 5 ? 'text-amber-600' : 'text-green-600'}`}>
                            {inventory <= 5 ? `Only ${inventory} left` : 'In Stock'}
                          </p>
                        ) : (
                          <p className="text-sm text-red-500/70">Out of Stock</p>
                        )}
                      </div>
                    )}

                    {/* Quantity Selector (only when product has variants and is in stock) */}
                    {hasVariants && !isOutOfStock && (
                      <QuantitySelector
                        quantity={quantity}
                        maxQuantity={maxQuantity}
                        onQuantityChange={handleQuantityChange}
                      />
                    )}
                  </div>
                )}

                {/* Description */}
                {product.description && (
                  <ProductDescription html={product.description} />
                )}

                {/* Vendor */}
                {product.vendor?.value && (
                  <div className="mb-3">
                    <span className="text-xs uppercase tracking-[0.15em] text-brand-navy/40 mr-2">
                      {product.vendor.name || 'Brand'}
                    </span>
                    <span className="text-sm text-brand-navy">
                      {product.vendor.value}
                    </span>
                  </div>
                )}

              </div>

              {/* Footer action bar */}
              <div className="border-t border-brand-gold/20 px-8 py-6 flex gap-3">
                {/* Add to Bag button — combo mode or standard mode (SPEC-036 AC-10, AC-16) */}
                {hasComboProducts ? (
                  /* Combo mode: disable while loading or if not all variants selected */
                  <button
                    onClick={handleAddComboBag}
                    disabled={comboLoading || !allComboVariantsSelected}
                    className={`flex-1 py-3 uppercase tracking-[0.15em] text-sm font-medium rounded-sm transition-colors flex items-center justify-center gap-2 ${
                      comboLoading || !allComboVariantsSelected
                        ? 'bg-brand-navy/20 text-white/60 cursor-not-allowed'
                        : 'bg-brand-navy text-white hover:bg-brand-navy/90'
                    }`}
                  >
                    {comboLoading ? (
                      'Loading...'
                    ) : (
                      <>
                        <ShoppingBagIcon className="w-4 h-4" />
                        Add Bundle to Bag
                      </>
                    )}
                  </button>
                ) : (
                  /* Standard mode: existing add-to-bag logic */
                  <button
                    onClick={handleAddToBag}
                    disabled={isOutOfStock || bagActionPending}
                    onMouseEnter={() => setReservedHovered(true)}
                    onMouseLeave={() => setReservedHovered(false)}
                    onFocus={() => setReservedHovered(true)}
                    onBlur={() => setReservedHovered(false)}
                    aria-label={
                      inCart && canCancelReserve ? 'Cancel reserve' : undefined
                    }
                    title={inCart && canCancelReserve ? 'Cancel reserve' : undefined}
                    className={`flex-1 py-3 uppercase tracking-[0.15em] text-sm font-medium rounded-sm transition-colors flex items-center justify-center gap-2 ${
                      inCart
                        ? `bg-brand-navy/80 text-white ${canCancelReserve ? 'cursor-pointer hover:bg-brand-navy/70' : ''}`
                        : isOutOfStock
                          ? 'bg-brand-navy/20 text-white/60 cursor-not-allowed'
                          : 'bg-brand-navy text-white hover:bg-brand-navy/90'
                    } ${bagActionPending ? 'opacity-70 cursor-wait' : ''}`}
                  >
                    {inCart ? (
                      /* QA2/BUG-2: reveal that "Reserved" is clickable-to-cancel on
                         hover/focus. Subtle label swap only — same size + icon slot. */
                      showCancelReserve ? (
                        <>
                          <XIcon className="w-4 h-4" />
                          Cancel reserve
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          Reserved
                        </>
                      )
                    ) : isOutOfStock ? (
                      'Out of Stock'
                    ) : (
                      <>
                        <ShoppingBagIcon className="w-4 h-4" />
                        Reserve
                      </>
                    )}
                  </button>
                )}

                {/* Share button */}
                {onShareProduct && product && (
                  <button
                    onClick={() => onShareProduct(product, shareVariant)}
                    className="relative group w-12 h-12 flex items-center justify-center border border-brand-gold/20 rounded-sm hover:bg-brand-navy/5 transition-colors"
                  >
                    <Share2Icon className="w-5 h-5" />
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-brand-navy text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      Share to chat
                    </span>
                  </button>
                )}

                {/* Favorite button */}
                <button
                  onClick={handleToggleFavorite}
                  className="relative group w-12 h-12 flex items-center justify-center border border-brand-gold/20 rounded-sm hover:bg-brand-navy/5 transition-colors"
                >
                  <HeartIcon
                    className="w-5 h-5"
                    fill={wishlisted ? 'currentColor' : 'none'}
                  />
                  <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-brand-navy text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                  </span>
                </button>
              </div>
            </>
          ) : null}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
